/**
 * PortalHostContext — the DOM node that JS-portaled overlays mount into.
 *
 * ── WHY THIS EXISTS (the app-scale dial) ──────────────────────────────────
 * The app-scale dial applies `zoom: var(--app-scale)` (125%) to the AppShellV2
 * root, scaling EVERYTHING inside the shell. But Radix `<Portal>` and hand-rolled
 * `createPortal(...)` default to `document.body`, which sits OUTSIDE the zoomed
 * subtree — so overlays rendered there stay at 100% and look ~20% too small next
 * to the zoomed app (measured: portal `currentCSSZoom` = 1 vs shell = 1.25).
 *
 * AppShellV2 publishes a host node that lives INSIDE its zoomed root here; the
 * vendored Radix primitives (ui/{dropdown-menu,tooltip,select}, common/{Dropdown,
 * Tooltip}) and the row-level delete modal read it and use it as their Portal
 * `container`. Overlays then mount inside the zoom (and inside the
 * `[data-theme="v2"]` token scope) and match the app's scale. Positioning is
 * unaffected — floating-ui reads the trigger's on-screen rect regardless of where
 * the floating element is parented.
 *
 * ── FALLBACK CONTRACT (non-v2 surfaces untouched) ─────────────────────────
 * Default is `null`. Outside the v2 shell (admin shell, auth pages) no provider
 * is mounted, so consumers resolve `null` and pass `container={undefined}` — and
 * Radix falls back to `document.body` exactly as before. Callers MUST coalesce a
 * `null` host to `undefined` (never pass `null` to a Radix Portal — `null` is a
 * real container value that breaks `createPortal`; `undefined` selects the body
 * default).
 */
import { createContext, useContext } from "react";

export const PortalHostContext = createContext<HTMLElement | null>(null);

/** Resolve the zoomed portal host (or `null` outside the v2 shell). */
export function usePortalHost(): HTMLElement | null {
  return useContext(PortalHostContext);
}
